import { useSyncExternalStore } from "react";

export const CURRENCIES = [
  { code: "USD", label: "US Dollar", symbol: "$" },
  { code: "EUR", label: "Euro", symbol: "€" },
  { code: "GBP", label: "British Pound", symbol: "£" },
  { code: "AUD", label: "Australian Dollar", symbol: "A$" },
  { code: "SGD", label: "Singapore Dollar", symbol: "S$" },
  { code: "CHF", label: "Swiss Franc", symbol: "CHF" },
  { code: "IDR", label: "Indonesian Rupiah", symbol: "Rp" },
];

const KEY = "pachamama.currency";

const TZ_RULES = [
  [/^Europe\/London|^Europe\/Belfast/, "GBP"],
  [/^Europe\/Zurich|^Europe\/Vaduz/, "CHF"],
  [/^Europe\//, "EUR"],
  [/^Australia\//, "AUD"],
  [/^Asia\/Singapore/, "SGD"],
  [/^Asia\/(Jakarta|Makassar|Jayapura|Pontianak)/, "IDR"],
];
const REGION_MAP = { GB: "GBP", AU: "AUD", SG: "SGD", CH: "CHF", LI: "CHF", ID: "IDR", US: "USD" };
const EUR_REGIONS = new Set("AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK".split(" "));
const EUR_LANGS = new Set(["de", "fr", "it", "es", "nl", "pt", "fi", "el", "et", "lv", "lt", "sk", "sl", "hr", "mt", "ga"]);

export const detectCurrency = () => {
  try {
    const lang = navigator.language || "";
    const [base, region] = lang.split("-");
    if (region) {
      if (REGION_MAP[region.toUpperCase()]) return REGION_MAP[region.toUpperCase()];
      if (EUR_REGIONS.has(region.toUpperCase())) return "EUR";
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    for (const [re, code] of TZ_RULES) if (re.test(tz)) return code;
    if (base === "id") return "IDR";
    if (EUR_LANGS.has(base)) return "EUR";
  } catch {}
  return "USD";
};

const stored = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
let state = {
  code: stored || detectCurrency(),
  autoDetected: !stored,
  rates: { USD: 1, EUR: 0.86, GBP: 0.74, AUD: 1.52, IDR: 16300, SGD: 1.28, CHF: 0.8 },
};
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

export const setCurrency = (code) => {
  state = { ...state, code, autoDetected: false };
  try {
    localStorage.setItem(KEY, code);
  } catch {}
  emit();
};

export const setRates = (rates) => {
  state = { ...state, rates: { ...state.rates, ...rates } };
  emit();
};

export const getCurrencyState = () => state;

export const useCurrency = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state
  );

export const convert = (usd) => usd * (state.rates[state.code] || 1);

export const formatMoney = (usd, opts = {}) => {
  const { code } = state;
  const value = convert(usd);
  if (code === "IDR") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "IDR",
      notation: opts.notation || (Math.abs(value) >= 1e7 ? "compact" : "standard"),
      maximumFractionDigits: Math.abs(value) >= 1e7 ? 2 : 0,
    }).format(value);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    notation: opts.notation || "standard",
    maximumFractionDigits: opts.notation === "compact" ? 1 : 0,
  }).format(value);
};
